import { RetirementMessageFromServer, I18nNotifications } from '../types/Notifications.types.js';

import TopNotificationAlertEvent, { EVENT_NAME as TopNotificationAlertEventName } from '../events/TopNotificationAlertEvent.js';

import { getRetiringData } from '../util/notification_helpers.js';
import { getData } from '../util/script_data.js';
import { updatePrefs } from '../util/user_prefs_util.js';

const DEFAULT_LOCALE = 'en-US';
const FAMILY_RETIRMENT_BANNER_TYPE = 'family';
const FOUNDRY_RETIRMENT_BANNER_TYPE = 'foundry';

class TopNotifications extends HTMLElement {
  retiringFamilies: RetirementMessageFromServer[] = [];
  retiringFoundries: RetirementMessageFromServer[] = [];
  retiringFamiliesSlugs: Set<string> = new Set();
  retiringFoundriesSlugs: Set<string> = new Set();
  locale: string = '';

  constructor() {
    super();
    this.locale = this.getAttribute('locale') || DEFAULT_LOCALE;
  }

  connectedCallback() {
    this.initializeRetiringData();
    this.setupDismissibleBanners();

    document.addEventListener(TopNotificationAlertEventName, this.handleTopNotificationAlert);
  }

  disconnectedCallback() {
    document.removeEventListener(TopNotificationAlertEventName, this.handleTopNotificationAlert);
  }

  initializeRetiringData() {
    const retiringData = getRetiringData();
    if (!retiringData) {
      return;
    }

    this.retiringFamilies = retiringData.retiringFamilies;
    this.retiringFoundries = retiringData.retiringFoundries;
    this.retiringFamiliesSlugs = retiringData.retiringFamiliesSlugs;
    this.retiringFoundriesSlugs = retiringData.retiringFoundriesSlugs;
  }

  /**
   * If a banner on page load is dismissible, we need to add a close event listener to its close button
   * to set the user preference for the banner on future page loads
   */
  setupDismissibleBanners() {
    if (this.getDismissibleBanners()) {
      this.getDismissibleBanners().forEach(banner => {
        const closeButton = banner.querySelector('[retirement-banner-close-button]');
        if (closeButton) {
          closeButton.addEventListener('click', this.handleClose, { once: true });
        }
      });
    }
  }

  getDismissibleBanners() {
    return this.querySelectorAll('[dismissible][top-notifications--banner]');
  }

  handleTopNotificationAlert = (event: Event) => {
    if (!(event instanceof TopNotificationAlertEvent)) {
      console.warn('Received unexpected event type:', event.type);
      return;
    }

    if (event.detail.retirement) {
      const { type, slug, name, synced } = event.detail.retirement;
      if (!this.retiringFamiliesSlugs.has(slug) && !this.retiringFoundriesSlugs.has(slug)) {
        return;
      }

      if (synced) {
        this.showRetirementBanner(slug, name, type);
      } else {
        this.checkAndHideRetirementBanner(slug, type);
      }
    }
  };

  checkAndHideRetirementBanner(slug: string, type: string) {
    const banner = this.querySelector(`[data-banner-type="${type}"][data-banner-slug="${slug}"]`);
    if (banner) {
      banner.remove();
    }
  }

  showRetirementBanner(slug: string, name: string, type: string) {
    const existingBanner = this.querySelector(`[data-banner-type="${type}"][data-banner-slug="${slug}"]`);
    if (existingBanner) {
      return;
    }

    const banner = this.createRetirementBanner(slug, name, type);
    if (!banner) {
      return;
    }

    this.appendChild(banner);
  }

  createRetirementBanner(slug: string, name: string, type: string) {
    const i18n = getData('/notifications/i18n');
    const template = this.querySelector('#retirement-banner-template') as HTMLTemplateElement;
    const banner = template?.content.cloneNode(true) as HTMLElement;

    const bannerElement = banner.querySelector('[top-notifications--banner]') as HTMLElement;
    bannerElement.setAttribute('data-banner-slug', slug);
    bannerElement.setAttribute('data-banner-type', type);

    const retirementDate = this.getRetirementDate(slug, type);
    const i18nTyped = i18n as I18nNotifications;

    const messageTemplate = i18nTyped.notifications?.retirement_banner?.message;
    if (!messageTemplate) {
      console.warn('Retirement banner message template is missing');
      return;
    }

    const localizedMessage = messageTemplate
      .replace('%{name}', name)
      .replace('%{retirement_date}', retirementDate || '');

    const bannerMessage = bannerElement.querySelector('.banner-message');
    if (bannerMessage) {
      bannerMessage.textContent = localizedMessage;
    }
    (bannerElement.querySelector('.banner-link') as HTMLAnchorElement).href = this.isFamilyRetirementBanner(type) ? `/fonts/${slug}` : `/foundries/${slug}`;

    const closeButton = bannerElement.querySelector('[retirement-banner-close-button]') as HTMLButtonElement;
    closeButton.setAttribute('data-banner-button-slug', slug);
    if (closeButton) {
      closeButton.addEventListener('click', this.handleClose);
    }

    return bannerElement;
  }

  getRetirementDate(slug: string, type: string) {
    const dateMap = this.isFamilyRetirementBanner(type)
      ? this.retiringFamilies
      : this.retiringFoundries;

    const item = dateMap.find((retired: RetirementMessageFromServer) => retired.slug === slug);

    return item ?
      new Date(item.date).toLocaleDateString(this.locale, {year: 'numeric', month: 'long', day: 'numeric'}) :
      ''
    ;
  }

  handleClose = (event: Event) => {
    const target = event.target as HTMLElement;
    const slug = target.getAttribute('data-banner-button-slug');
    const banner = document.querySelector(`[data-banner-slug="${slug}"]`) as HTMLElement;
    if (!banner) {
      return;
    }

    const type = banner.getAttribute('data-banner-type') || '';
    if (this.isFamilyRetirementBanner(type)) {
      this.updatePrefs('hideFamilyRetirementNotification', Array.from(this.retiringFamiliesSlugs).join(','));
    } else if (type === FOUNDRY_RETIRMENT_BANNER_TYPE) {
      this.updatePrefs('hideFoundryRetirementNotification', Array.from(this.retiringFoundriesSlugs).join(','));
    }

    banner.removeAttribute('open');
  };

  isFamilyRetirementBanner(type: string) {
    return type === FAMILY_RETIRMENT_BANNER_TYPE;
  }

  updatePrefs(key: string, value: string) {
    updatePrefs({ [key]: value });
  }
}

customElements.define('af-top-notifications', TopNotifications);

export { TopNotifications };
